/**
 * Everything the Evals page reads and writes goes through the API (obsessless-api
 * src/routes/evals). Firestore denies all client access to the eval collections.
 *
 * The routes exist only where the API has EVALS_ENABLED=true: staging and a local API.
 * Against production they answer `not-found`.
 */
import { createApiCall } from '../../../services/apiClient';
import type {
  DecidedSuggestion,
  EvalCaseContent,
  EvalCaseDetail,
  EvalCaseInfo,
  EvalPromptResponse,
  EvalReview,
  EvalReviewQueue,
  EvalRunDetail,
  EvalRunItem,
  EvalRunPage,
  JudgeFlag,
  JudgePromptInfo,
  ListEvalSuitesResponse,
  RegradeReport,
  RegradeScope,
  RunEvalRequest,
} from '../types/evalTypes';

const listSuitesCall = createApiCall<void, ListEvalSuitesResponse>('adminListEvalSuites');
const getPromptCall = createApiCall<{ suiteId: string }, EvalPromptResponse>('adminGetEvalPrompt');
const listRunsCall = createApiCall<{ limit?: number; before?: number }, EvalRunPage>('adminListEvalRuns');
const getReviewQueueCall = createApiCall<void, EvalReviewQueue>('adminGetEvalReviewQueue');
const getRunCall = createApiCall<{ runId: string }, EvalRunDetail>('adminGetEvalRun');
const startRunCall = createApiCall<RunEvalRequest, { runId: string; total: number }>('adminStartEvalRun');
const cancelRunCall = createApiCall<{ runId: string }, { run: EvalRunItem }>('adminCancelEvalRun');
const startRegradeCall = createApiCall<
  { sourceRunId: string; scope: RegradeScope; judgePromptVersions?: Record<string, string> },
  { runId: string; total: number }
>('adminStartRegrade');
const getRegradeReportCall = createApiCall<{ runId: string }, RegradeReport>('adminGetRegradeReport');
const listJudgePromptsCall = createApiCall<void, { judgePrompts: JudgePromptInfo[] }>('adminListJudgePrompts');
const createJudgePromptCall = createApiCall<
  { suiteId: string; judgeName: string; text: string; note: string; makeCurrent: boolean },
  { judgePrompt: JudgePromptInfo }
>('adminCreateJudgePromptVersion');
const setCurrentJudgePromptCall = createApiCall<{ suiteId: string; judgeName: string; versionId: string }, { judgePrompt: JudgePromptInfo }>(
  'adminSetCurrentJudgePrompt',
);
const listCasesCall = createApiCall<{ suiteId: string }, { cases: EvalCaseInfo[] }>('adminListEvalCases');
const getCaseCall = createApiCall<{ suiteId: string; caseId: string }, { evalCase: EvalCaseDetail }>('adminGetEvalCase');
const createCaseVersionCall = createApiCall<
  { suiteId: string; caseId: string; content: EvalCaseContent; note: string; makeCurrent: boolean; newCase: boolean },
  { evalCase: EvalCaseDetail }
>('adminCreateEvalCaseVersion');
const setCurrentCaseCall = createApiCall<{ suiteId: string; caseId: string; versionId: string }, { evalCase: EvalCaseDetail }>(
  'adminSetCurrentEvalCase',
);
const setCaseArchivedCall = createApiCall<{ suiteId: string; caseId: string; archived: boolean }, { evalCase: EvalCaseDetail }>(
  'adminSetEvalCaseArchived',
);
const setCaseVersionArchivedCall = createApiCall<
  { suiteId: string; caseId: string; versionId: string; archived: boolean; reason: string },
  { evalCase: EvalCaseDetail }
>('adminSetEvalCaseVersionArchived');
const setJudgePromptVersionArchivedCall = createApiCall<
  { suiteId: string; judgeName: string; versionId: string; archived: boolean; reason: string },
  { judgePrompt: JudgePromptInfo }
>('adminSetJudgePromptVersionArchived');
const setReviewCall = createApiCall<{ runId: string; decision: EvalReview['decision']; note: string }, { review: EvalReview }>(
  'adminSetEvalReview',
);
const setJudgeFlagCall = createApiCall<
  { runId: string; resultId: string; checkIds: string[]; note: string },
  { judgeFlag: JudgeFlag | null }
>('adminSetEvalJudgeFlag');
const decideSuggestionCall = createApiCall<
  { runId: string; suggestionId: string; accept: boolean; rejectReason: string },
  DecidedSuggestion
>('adminDecideEvalSuggestion');

class EvalsService {
  private static instance: EvalsService;

  private constructor() {}

  public static getInstance(): EvalsService {
    if (!EvalsService.instance) {
      EvalsService.instance = new EvalsService();
    }
    return EvalsService.instance;
  }

  public async listSuites(): Promise<ListEvalSuitesResponse> {
    return listSuitesCall(undefined);
  }

  /** The suite's production prompt. Only fetched when a draft is started. */
  public async getPrompt(suiteId: string): Promise<EvalPromptResponse> {
    return getPromptCall({ suiteId });
  }

  /** Newest first, one page. `before` is the `nextBefore` of the previous page. */
  public async listRuns(limit: number, before?: number): Promise<EvalRunPage> {
    return listRunsCall({ limit, ...(before !== undefined && { before }) });
  }

  /** Suggestions, cases and judge prompt versions that wait for a human. */
  public async getReviewQueue(): Promise<EvalReviewQueue> {
    return getReviewQueueCall(undefined);
  }

  public async getRun(runId: string): Promise<EvalRunDetail> {
    return getRunCall({ runId });
  }

  public async setReview(runId: string, decision: EvalReview['decision'], note: string): Promise<EvalReview> {
    const { review } = await setReviewCall({ runId, decision, note });
    return review;
  }

  /** An empty `checkIds` clears the flag. */
  public async setJudgeFlag(runId: string, resultId: string, checkIds: string[], note: string): Promise<JudgeFlag | null> {
    const { judgeFlag } = await setJudgeFlagCall({ runId, resultId, checkIds, note });
    return judgeFlag;
  }

  /**
   * Accepts or rejects a suggestion from Claude. Accepting sets the judge flag (merged with
   * the result's flag) or the run review, signed by the caller.
   */
  public async decideSuggestion(runId: string, suggestionId: string, accept: boolean, rejectReason = ''): Promise<DecidedSuggestion> {
    return decideSuggestionCall({ runId, suggestionId, accept, rejectReason });
  }

  /** Every judge of every suite, with all versions. The API creates v1 from the code the first time. */
  public async listJudgePrompts(): Promise<JudgePromptInfo[]> {
    const { judgePrompts } = await listJudgePromptsCall(undefined);
    return judgePrompts;
  }

  /** Fails with `already-exists` when a version has the same text. */
  public async createJudgePromptVersion(
    suiteId: string,
    judgeName: string,
    version: { text: string; note: string; makeCurrent: boolean },
  ): Promise<JudgePromptInfo> {
    const { judgePrompt } = await createJudgePromptCall({ suiteId, judgeName, ...version });
    return judgePrompt;
  }

  /** Runs queued from now on use this version. */
  public async setCurrentJudgePrompt(suiteId: string, judgeName: string, versionId: string): Promise<JudgePromptInfo> {
    const { judgePrompt } = await setCurrentJudgePromptCall({ suiteId, judgeName, versionId });
    return judgePrompt;
  }

  /** Every case of the suite, archived and proposed ones too, by case ID. */
  public async listCases(suiteId: string): Promise<EvalCaseInfo[]> {
    const { cases } = await listCasesCall({ suiteId });
    return cases;
  }

  public async getCase(suiteId: string, caseId: string): Promise<EvalCaseDetail> {
    const { evalCase } = await getCaseCall({ suiteId, caseId });
    return evalCase;
  }

  /**
   * Saves a new version, or a new case when `caseId` is new. Fails with `invalid-argument`
   * for content the suite refuses, and `already-exists` when a version has the same content
   * or, with `newCase`, when the case exists.
   */
  public async createCaseVersion(
    suiteId: string,
    caseId: string,
    version: { content: EvalCaseContent; note: string; makeCurrent: boolean; newCase: boolean },
  ): Promise<EvalCaseDetail> {
    const { evalCase } = await createCaseVersionCall({ suiteId, caseId, ...version });
    return evalCase;
  }

  /** Runs queued from now on use this version. */
  public async setCurrentCase(suiteId: string, caseId: string, versionId: string): Promise<EvalCaseDetail> {
    const { evalCase } = await setCurrentCaseCall({ suiteId, caseId, versionId });
    return evalCase;
  }

  /** Archives a version that is not current, with an optional reason, or restores it (`archived: false`). */
  public async setCaseVersionArchived(suiteId: string, caseId: string, versionId: string, archived: boolean, reason = ''): Promise<EvalCaseDetail> {
    const { evalCase } = await setCaseVersionArchivedCall({ suiteId, caseId, versionId, archived, reason });
    return evalCase;
  }

  /** Archives a judge prompt version that is not current, with an optional reason, or restores it. */
  public async setJudgePromptVersionArchived(suiteId: string, judgeName: string, versionId: string, archived: boolean, reason = ''): Promise<JudgePromptInfo> {
    const { judgePrompt } = await setJudgePromptVersionArchivedCall({ suiteId, judgeName, versionId, archived, reason });
    return judgePrompt;
  }

  public async setCaseArchived(suiteId: string, caseId: string, archived: boolean): Promise<EvalCaseDetail> {
    const { evalCase } = await setCaseArchivedCall({ suiteId, caseId, archived });
    return evalCase;
  }

  /** Queues a run. It runs in the background on the API; follow it with getRun. */
  public async startRun(request: RunEvalRequest): Promise<{ runId: string; total: number }> {
    return startRunCall(request);
  }

  /** Queues a re-grade: the run's outputs graded again, with no model calls. */
  public async startRegrade(
    sourceRunId: string,
    scope: RegradeScope,
    judgePromptVersions: Record<string, string>,
  ): Promise<{ runId: string; total: number }> {
    return startRegradeCall({ sourceRunId, scope, ...(Object.keys(judgePromptVersions).length > 0 && { judgePromptVersions }) });
  }

  public async getRegradeReport(runId: string): Promise<RegradeReport> {
    return getRegradeReportCall({ runId });
  }

  /** Cancels a queued run at once, and stops a running one within a few seconds. */
  public async cancelRun(runId: string): Promise<EvalRunItem> {
    const { run } = await cancelRunCall({ runId });
    return run;
  }
}

export const evalsService = EvalsService.getInstance();
